import type { CreateRecordType } from './recordTypes';

/**
 * The mutations a create form saves its record with. A save runs one of them: which one is the form's choice
 * (a timecard is a draft, a finished one, one per author, or one added to an invoice).
 */
const CREATE_ENDPOINTS: Record<CreateRecordType, readonly string[]> = {
    case: ['addMatter'],
    contact: ['addContact'],
    timecard: [
        'addTimecard',
        'finishTimecard',
        'finishMultiAuthorTimecards',
        'addInvoiceTimecard',
        'addMultiAuthorInvoiceTimecards',
    ],
    expense: ['addExpense'],
    contract: ['addContract'],
    'client-asset': ['addPartyAsset'],
    correspondence: ['addCorrespondence'],
    debriefing: ['addDebriefing'],
    'experience-record': ['addExperienceRecord'],
    task: ['addTask'],
    'bd-effort': ['addEffort'],
    project: ['addProject'],
    training: ['addTraining'],
    'marketing-profile': ['createMarketingProfile'],
    employee: ['addEmployee'],
    group: ['addPartyGroup'],
    team: ['addTeam'],
    office: ['addOffice'],
    'practice-area': ['addPracticeArea'],
    'job-role': ['addJobRole'],
    'job-position': ['addJobPosition'],
};

/** The creation under way in the create dialog, as the mutation toast needs it. */
export type ActiveCreation = {
    record: CreateRecordType;
    /**
     * Goes to the record this creation ends with. The form says where that is once it has finished, which is
     * after its mutation's toast is shown, so this does nothing until then. `null` while a popup is open over
     * the dialog: what is saved meanwhile is the popup's own record.
     */
    open: (() => void) | null;
};

let active: ActiveCreation | null = null;

/** The create dialog's host says which creation is under way, and `null` when none is. */
export function setActiveCreation(creation: ActiveCreation | null): void {
    active = creation;
}

/** The mutation a creation is told of as: a draft's completion creates its record, as that record's own create does. */
export function createEndpointOf(endpointName: string, originalArgs: unknown): string {
    const type = (originalArgs as { type?: CreateRecordType } | undefined)?.type;
    return endpointName === 'completeDraft' && type && Object.hasOwn(CREATE_ENDPOINTS, type)
        ? CREATE_ENDPOINTS[type][0]
        : endpointName;
}

/** The creation under way in the create dialog, when `endpointName` is a mutation that saves its record. */
export function activeCreationOf(endpointName: string): ActiveCreation | null {
    return active && CREATE_ENDPOINTS[active.record].includes(endpointName) ? active : null;
}
